import React, { useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Typography, Alert, Table, TableHead, TableRow, TableCell, TableBody, TextField } from "@mui/material";
import { RET_IR_OPTIONS, RET_IVA_OPTIONS } from "../../Empresa/NuevaFactura/InvoiceRetention/Retention";

const getCookie = (name) => {
  const v = `; ${document.cookie}`;
  const parts = v.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const num2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const formatDoc3_3_9 = (s) => {
  const d = onlyDigits(s);
  if (d.length === 15) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return String(s || "");
};

export default function InvoiceRetentionUpload({ documentoId, onSaved }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [match, setMatch] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const mapIR = useMemo(() => {
    const m = new Map();
    RET_IR_OPTIONS.forEach((x) => m.set(String(x.codigo).trim(), x.descripcion));
    return m;
  }, []);

  const mapIVA = useMemo(() => {
    const m = new Map();
    RET_IVA_OPTIONS.forEach((x) => m.set(String(x.codigo).trim(), x.descripcion));
    return m;
  }, []);

  const retRows = useMemo(() => {
    const arr = parsed?.retenciones || [];
    return Array.isArray(arr) ? arr : [];
  }, [parsed]);

  const totalRet = useMemo(() => retRows.reduce((a, r) => a + Number(r.valor || 0), 0), [retRows]);

  const canSave = useMemo(() => Boolean(parsed) && match === true, [parsed, match]);

  const ensureCsrf = async () => {
    await fetch("/facturas/csrf/", { credentials: "include" }).catch(() => {});
  };

  const getDescripcion = (r) => {
    const tipo = String(r.codigo || "").trim();
    const cod = String(r.codigo_retencion || "").trim();
    if (tipo === "2") return mapIVA.get(cod) || `Otras retenciones aplicables código ${cod}`;
    return mapIR.get(cod) || `Otras retenciones aplicables código ${cod}`;
  };

  const preview = async () => {
    if (!file) {
      setMsg("Selecciona un XML primero.");
      return;
    }
    if (!documentoId) {
      setMsg("No tengo documentoId para guardar en tu BD.");
      return;
    }

    setBusy(true);
    setMsg("");
    setParsed(null);
    setMatch(null);

    try {
      await ensureCsrf();
      const csrftoken = getCookie("csrftoken");

      const fd = new FormData();
      fd.append("documento_id", String(documentoId));
      fd.append("xml", file);

      const r = await fetch("/facturas/retenciones/preview/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrftoken },
        body: fd,
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || `Preview falló (${r.status})`);

      const p = data.parsed || null;
      setParsed(p);
      setMatch(Boolean(data.match));
      setMsg(data.match ? "OK: La retención corresponde a esta factura." : (data.match_msg || "No coincide"));
    } catch (e) {
      setMsg(e.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const guardar = async () => {
    if (!canSave) {
      setMsg("No puedes guardar: primero previsualiza y valida que corresponda.");
      return;
    }
    if (!documentoId) {
      setMsg("No tengo documentoId para guardar en tu BD.");
      return;
    }

    setBusy(true);
    setMsg("");

    try {
      await ensureCsrf();
      const csrftoken = getCookie("csrftoken");

      const fd = new FormData();
      fd.append("documento_id", String(documentoId));
      fd.append("payload", JSON.stringify(parsed));

      const r = await fetch("/facturas/retenciones/guardar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrftoken },
        body: fd,
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || `Guardar falló (${r.status})`);

      setMsg(`Guardado. Saldo: ${data.saldo} | Retenciones: ${data.retenciones_total}`);
      if (onSaved) onSaved();
    } catch (e) {
      setMsg(e.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const retFecha = parsed?.retencion?.fecha_emision || "-";
  const retNum = parsed?.retencion?.numero_documento || "-";
  const retAut = parsed?.autorizacion?.numero_autorizacion || "-";

  const sustentoFmt =
    parsed?.sustento?.num_doc_sustento_fmt ||
    formatDoc3_3_9(parsed?.sustento?.num_doc_sustento || "");

  const claveSust = parsed?.sustento?.num_aut_doc_sustento || "-";

  return (
    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>Cargar XML de Retención</Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
        <input
          type="file"
          accept=".xml,text/xml,application/xml"
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            setParsed(null);
            setMatch(null);
            setMsg("");
          }}
        />

        <Button variant="contained" disabled={busy || !file} onClick={preview}>
          {busy ? "Procesando..." : "PREVISUALIZAR"}
        </Button>

        <Button variant="contained" color="success" disabled={busy || !canSave} onClick={guardar}>
          GUARDAR
        </Button>
      </Stack>

      {msg ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity={match === false ? "error" : "info"}>{msg}</Alert>
        </Box>
      ) : null}

      {parsed ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 800 }}>
            Fecha de Emisión: <span style={{ fontWeight: 600 }}>{retFecha}</span>
          </Typography>
          <Typography sx={{ fontWeight: 800 }}>
            Número de Documento: <span style={{ fontWeight: 600 }}>{retNum}</span>
          </Typography>
          <Typography sx={{ fontWeight: 800 }}>
            Autorización: <span style={{ fontWeight: 600 }}>{retAut}</span>
          </Typography>

          <Typography sx={{ mt: 1, color: "#666" }}>
            Sustento: {sustentoFmt || "-"} | Clave sustento: {claveSust || "-"}
          </Typography>
        </Box>
      ) : null}

      {parsed ? (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ border: "1px solid #e6e6e6", borderRadius: 1.5, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Retención</TableCell>
                  <TableCell sx={{ width: 130, fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ width: 120, fontWeight: 700 }}>Cod. SRI</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 700 }}>Base</TableCell>
                  <TableCell sx={{ width: 90, fontWeight: 700 }}>%</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 700 }}>Valor</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {retRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography sx={{ py: 2, color: "#6b7280" }}>No se encontraron retenciones en el XML.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  retRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          value={getDescripcion(r)}
                          fullWidth
                          size="small"
                          disabled
                          sx={{
                            "& .Mui-disabled": {
                              backgroundColor: "#fff !important",
                              WebkitTextFillColor: "#111827 !important",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ color: "#111827" }}>
                          {String(r.codigo) === "2" ? "IVA" : "Imp. a la Renta"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ color: "#111827" }}>{r.codigo_retencion}</Typography>
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={num2(r.base_imponible)}
                          size="small"
                          disabled
                          fullWidth
                          inputProps={{ style: { textAlign: "right" } }}
                          sx={{
                            "& .Mui-disabled": {
                              backgroundColor: "#f3f4f6 !important",
                              WebkitTextFillColor: "#111827 !important",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={num2(r.porcentaje)}
                          size="small"
                          disabled
                          fullWidth
                          inputProps={{ style: { textAlign: "right" } }}
                          sx={{
                            "& .Mui-disabled": {
                              backgroundColor: "#f3f4f6 !important",
                              WebkitTextFillColor: "#111827 !important",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={num2(r.valor)}
                          size="small"
                          disabled
                          fullWidth
                          inputProps={{ style: { textAlign: "right" } }}
                          sx={{
                            "& .Mui-disabled": {
                              backgroundColor: "#f3f4f6 !important",
                              WebkitTextFillColor: "#111827 !important",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {retRows.length > 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "right", fontWeight: 700 }}>
                      Total retenciones (XML)
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={num2(totalRet)}
                        size="small"
                        disabled
                        fullWidth
                        inputProps={{ style: { textAlign: "right", fontWeight: 700 } }}
                        sx={{
                          "& .Mui-disabled": {
                            backgroundColor: "#f3f4f6 !important",
                            WebkitTextFillColor: "#111827 !important",
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      ) : null}
    </Paper>
  );
}
